import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Skeleton,
  Stat,
  StatLabel,
  StatNumber,
  Switch,
  Text,
  Tooltip,
  useColorModeValue,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  BsArrowDown,
  BsCircleFill,
  BsPause,
  BsPlay,
  BsSearch,
  BsTerminal,
  BsTrash,
} from "react-icons/bs";
import terminalApi from "../services/terminalApi";

const LEVEL_CONFIG = {
  INFO: { color: "cyan.400", bg: "cyan.900", label: "INFO " },
  WARN: { color: "yellow.400", bg: "yellow.900", label: "WARN " },
  ERROR: { color: "red.400", bg: "red.900", label: "ERROR" },
  DEBUG: { color: "gray.400", bg: "gray.700", label: "DEBUG" },
};

const MAX_VISIBLE_LOGS = 1000;

const LogEntry = React.memo(({ entry }) => {
  const cfg = LEVEL_CONFIG[entry.level] || LEVEL_CONFIG.INFO;
  const ts = new Date(entry.timestamp);
  const time = ts.toLocaleTimeString("en-US", { hour12: false });

  return (
    <Box
      fontFamily="'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace"
      fontSize="12.5px"
      lineHeight="20px"
      px={3}
      py="1px"
      _hover={{ bg: "whiteAlpha.50" }}
      whiteSpace="pre-wrap"
      wordBreak="break-all"
    >
      <Text as="span" color="gray.500">
        {time}
      </Text>{" "}
      <Badge
        fontSize="10px"
        fontFamily="inherit"
        px={1}
        py={0}
        borderRadius="sm"
        bg={cfg.bg}
        color={cfg.color}
        fontWeight="bold"
        letterSpacing="0.5px"
      >
        {cfg.label}
      </Badge>{" "}
      <Text as="span" color={cfg.color === "cyan.400" ? "gray.100" : cfg.color}>
        {entry.message}
      </Text>
    </Box>
  );
});
LogEntry.displayName = "LogEntry";

const TerminalLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [streaming, setStreaming] = useState(true);
  const [autoScroll, setAutoScroll] = useState(true);
  const [filterLevel, setFilterLevel] = useState("");
  const [searchText, setSearchText] = useState("");
  const [stats, setStats] = useState(null);
  const [connected, setConnected] = useState(false);

  const scrollRef = useRef(null);
  const eventSourceRef = useRef(null);
  const toast = useToast();

  // Load initial logs
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [logRes, statsRes] = await Promise.allSettled([
          terminalApi.getLiveLogs({ lines: 500 }),
          terminalApi.getServerStats(),
        ]);
        if (logRes.status === "fulfilled") {
          setLogs(logRes.value.entries || []);
        }
        if (statsRes.status === "fulfilled") {
          setStats(statsRes.value);
        }
      } catch {
        toast({
          title: "Failed to load logs",
          status: "error",
          duration: 3000,
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [toast]);

  // SSE streaming
  useEffect(() => {
    if (!streaming) {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
        setConnected(false);
      }
      return;
    }

    const streamUrl = terminalApi.getStreamUrl();
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    // EventSource doesn't support custom headers, so pass token as query param
    const url = token
      ? `${streamUrl}?token=${encodeURIComponent(token)}`
      : streamUrl;
    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.onopen = () => setConnected(true);
    es.onmessage = (event) => {
      try {
        const entry = JSON.parse(event.data);
        setLogs((prev) => {
          const next = [...prev, entry];
          return next.length > MAX_VISIBLE_LOGS
            ? next.slice(-MAX_VISIBLE_LOGS)
            : next;
        });
      } catch {
        // ignore malformed
      }
    };
    es.onerror = () => {
      setConnected(false);
      // EventSource will auto-reconnect
    };

    return () => {
      es.close();
      setConnected(false);
    };
  }, [streaming]);

  // Auto-scroll
  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  const clearLogs = useCallback(() => setLogs([]), []);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  // Filter logs
  const filteredLogs = React.useMemo(() => {
    let result = logs;
    if (filterLevel) {
      result = result.filter((e) => e.level === filterLevel);
    }
    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      result = result.filter((e) => e.message.toLowerCase().includes(lower));
    }
    return result;
  }, [logs, filterLevel, searchText]);

  // Counts
  const counts = React.useMemo(() => {
    const c = { INFO: 0, WARN: 0, ERROR: 0, DEBUG: 0 };
    for (const e of logs) c[e.level] = (c[e.level] || 0) + 1;
    return c;
  }, [logs]);

  const termBg = useColorModeValue("gray.900", "gray.950");

  return (
    <Box
      p={6}
      maxW="100%"
      minH="100vh"
      bg={useColorModeValue("gray.50", "gray.900")}
    >
      {/* Header */}
      <Flex mb={4} align="center" justify="space-between" wrap="wrap" gap={3}>
        <HStack spacing={3}>
          <Icon as={BsTerminal} boxSize={6} color="teal.400" />
          <Heading size="lg" color={useColorModeValue("gray.800", "white")}>
            Server Terminal
          </Heading>
          <HStack spacing={1}>
            <Icon
              as={BsCircleFill}
              boxSize={2}
              color={connected ? "green.400" : "red.400"}
            />
            <Text fontSize="xs" color="gray.500">
              {connected ? "Live" : "Disconnected"}
            </Text>
          </HStack>
        </HStack>

        {stats && (
          <HStack spacing={4} display={{ base: "none", lg: "flex" }}>
            <SimpleGrid columns={3} spacing={4}>
              <Stat size="sm">
                <StatLabel fontSize="xs" color="gray.500">
                  Uptime
                </StatLabel>
                <StatNumber fontSize="sm">
                  {stats.process?.uptimeFormatted || "-"}
                </StatNumber>
              </Stat>
              <Stat size="sm">
                <StatLabel fontSize="xs" color="gray.500">
                  Heap Used
                </StatLabel>
                <StatNumber fontSize="sm">
                  {stats.process?.memory?.heapUsed || "-"}
                </StatNumber>
              </Stat>
              <Stat size="sm">
                <StatLabel fontSize="xs" color="gray.500">
                  Node
                </StatLabel>
                <StatNumber fontSize="sm">
                  {stats.process?.nodeVersion || "-"}
                </StatNumber>
              </Stat>
            </SimpleGrid>
          </HStack>
        )}
      </Flex>

      {/* Level badges / counts */}
      <HStack spacing={2} mb={3} flexWrap="wrap">
        {Object.entries(counts).map(([level, count]) => {
          const cfg = LEVEL_CONFIG[level];
          return (
            <Badge
              key={level}
              px={2}
              py={0.5}
              borderRadius="md"
              bg={cfg.bg}
              color={cfg.color}
              cursor="pointer"
              border="1px solid"
              borderColor={filterLevel === level ? cfg.color : "transparent"}
              onClick={() =>
                setFilterLevel((prev) => (prev === level ? "" : level))
              }
              _hover={{ opacity: 0.8 }}
            >
              {level}: {count}
            </Badge>
          );
        })}
        {filterLevel && (
          <Button
            size="xs"
            variant="ghost"
            color="gray.400"
            onClick={() => setFilterLevel("")}
          >
            Clear filter
          </Button>
        )}
      </HStack>

      {/* Toolbar */}
      <Flex
        mb={2}
        gap={2}
        align="center"
        wrap="wrap"
        bg={useColorModeValue("gray.800", "gray.800")}
        px={3}
        py={2}
        borderTopRadius="lg"
        borderBottom="1px solid"
        borderColor="whiteAlpha.100"
      >
        <InputGroup size="sm" maxW="280px">
          <InputLeftElement pointerEvents="none">
            <BsSearch color="gray" />
          </InputLeftElement>
          <Input
            placeholder="Search logs…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            bg="whiteAlpha.100"
            border="1px solid"
            borderColor="whiteAlpha.200"
            color="white"
            _placeholder={{ color: "gray.500" }}
            _focus={{ borderColor: "teal.400" }}
          />
        </InputGroup>

        <Select
          size="sm"
          maxW="130px"
          value={filterLevel}
          onChange={(e) => setFilterLevel(e.target.value)}
          bg="whiteAlpha.100"
          border="1px solid"
          borderColor="whiteAlpha.200"
          color="white"
        >
          <option value="" style={{ background: "#1a202c" }}>
            All Levels
          </option>
          <option value="INFO" style={{ background: "#1a202c" }}>
            INFO
          </option>
          <option value="WARN" style={{ background: "#1a202c" }}>
            WARN
          </option>
          <option value="ERROR" style={{ background: "#1a202c" }}>
            ERROR
          </option>
          <option value="DEBUG" style={{ background: "#1a202c" }}>
            DEBUG
          </option>
        </Select>

        <HStack spacing={1} ml="auto">
          <HStack spacing={1}>
            <Text fontSize="xs" color="gray.400">
              Auto-scroll
            </Text>
            <Switch
              size="sm"
              isChecked={autoScroll}
              onChange={(e) => setAutoScroll(e.target.checked)}
              colorScheme="teal"
            />
          </HStack>

          <Tooltip label={streaming ? "Pause stream" : "Resume stream"}>
            <IconButton
              size="sm"
              variant="ghost"
              color={streaming ? "green.400" : "gray.400"}
              icon={<Icon as={streaming ? BsPause : BsPlay} />}
              onClick={() => setStreaming(!streaming)}
              aria-label="Toggle streaming"
            />
          </Tooltip>

          <Tooltip label="Scroll to bottom">
            <IconButton
              size="sm"
              variant="ghost"
              color="gray.400"
              icon={<Icon as={BsArrowDown} />}
              onClick={scrollToBottom}
              aria-label="Scroll to bottom"
            />
          </Tooltip>

          <Tooltip label="Clear logs">
            <IconButton
              size="sm"
              variant="ghost"
              color="gray.400"
              icon={<Icon as={BsTrash} />}
              onClick={clearLogs}
              aria-label="Clear logs"
            />
          </Tooltip>
        </HStack>
      </Flex>

      {/* Terminal viewport */}
      <Box
        ref={scrollRef}
        bg={termBg}
        borderBottomRadius="lg"
        overflowY="auto"
        overflowX="hidden"
        h="calc(100vh - 280px)"
        minH="400px"
        border="1px solid"
        borderColor="whiteAlpha.100"
        borderTop="none"
        css={{
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "#4a5568",
            borderRadius: "3px",
          },
        }}
      >
        {loading ? (
          <VStack p={6} spacing={2}>
            {[...Array(8)].map((_, i) => (
              <Skeleton
                key={i}
                h="20px"
                w={`${60 + Math.random() * 40}%`}
                startColor="gray.800"
                endColor="gray.700"
              />
            ))}
          </VStack>
        ) : filteredLogs.length === 0 ? (
          <Flex justify="center" align="center" h="200px">
            <VStack>
              <Icon as={BsTerminal} boxSize={8} color="gray.600" />
              <Text color="gray.500" fontSize="sm">
                {searchText || filterLevel
                  ? "No logs match your filter"
                  : "Waiting for logs…"}
              </Text>
            </VStack>
          </Flex>
        ) : (
          <Box py={1}>
            {filteredLogs.map((entry) => (
              <LogEntry key={entry.id} entry={entry} />
            ))}
          </Box>
        )}
      </Box>

      {/* Footer status bar */}
      <Flex
        mt={1}
        justify="space-between"
        px={3}
        py={1}
        bg={useColorModeValue("gray.800", "gray.800")}
        borderRadius="md"
      >
        <Text fontSize="xs" color="gray.500">
          {filteredLogs.length} entries
          {filterLevel && ` (filtered: ${filterLevel})`}
        </Text>
        <Text fontSize="xs" color="gray.500">
          Buffer: {logs.length}/{MAX_VISIBLE_LOGS}
        </Text>
      </Flex>
    </Box>
  );
};

export default TerminalLogs;
